"use client"

import { useState } from "react"
import { X } from "lucide-react"
import { File, Layers, Suitcase } from "nucleo-glass-icons/react"
import type { ComponentType } from "react"
import type { IconProps } from "nucleo-glass-icons/react"

import { CookieGlyph, openCookieSettings } from "./cookie-consent"
import styles from "./footer-section.module.css"

// Site-wide footer. `surface` themes it dark (marketing) or light (SEO pages).
// Grid of real-page columns only (no placeholder links). Company links point
// at the /companies hub plus a few marquee names — the hub carries all 50
// (hub-and-spoke), so the footer never becomes a 50-link wall.

type FooterLink = { name: string; href: string; modal?: boolean; cookieSettings?: boolean }
type FooterColumn = {
  title: string
  Icon: ComponentType<IconProps>
  links: FooterLink[]
}

const COLUMNS: FooterColumn[] = [
  {
    title: "Explore",
    Icon: Layers,
    links: [
      { name: "Trial Tracker", href: "/" },
      { name: "Drug Pipelines", href: "/pipeline" },
      { name: "Companies", href: "/companies" },
      { name: "Articles", href: "/articles" },
      { name: "Free Pipeline Tool", href: "/tools/trial-pipeline" },
    ],
  },
  {
    title: "Popular companies",
    Icon: Suitcase,
    links: [
      { name: "Pfizer", href: "/companies/pfizer" },
      { name: "Novartis", href: "/companies/novartis" },
      { name: "AstraZeneca", href: "/companies/astrazeneca" },
      { name: "Eli Lilly", href: "/companies/eli-lilly" },
      { name: "Novo Nordisk", href: "/companies/novo-nordisk" },
      { name: "All 50 companies", href: "/companies" },
    ],
  },
  {
    title: "Company",
    Icon: File,
    links: [
      { name: "About", href: "/about" },
      { name: "Contact", href: "/contact" },
      { name: "Support", href: "#support", modal: true },
      { name: "Privacy Policy", href: "/privacy-policy" },
      { name: "Cookie Policy", href: "/cookie-policy" },
      // FE-COOKIE-1 (Paper 1OG-0): re-opens the cookie preferences dialog.
      { name: "Cookie settings", href: "#cookie-settings", cookieSettings: true },
      { name: "Terms of Service", href: "/terms-of-service" },
    ],
  },
]

// Glass-icon gradient stops per surface: dark ink on light footers, pale on
// dark footers, so the duotone reads either way.
const ICON_STOPS_LIGHT: Pick<IconProps, "stopColor1" | "stopColor2"> = {
  stopColor1: "#575757",
  stopColor2: "#151515",
}
const ICON_STOPS_DARK: Pick<IconProps, "stopColor1" | "stopColor2"> = {
  stopColor1: "#E8E9DE",
  stopColor2: "#9A9C90",
}

/**
 * The footer's "Cookie settings" control (Paper 1OG-0): a 16px cookie glyph,
 * gap 8, 14/20 500, underlined. A button, since it opens a dialog. Colour
 * follows the footer surface, not Paper's #17181B, so it reads on the dark
 * footer too. Presentational: the footer passes openCookieSettings, and the
 * localhost preview passes a preview-local handler that never reaches storage.
 */
export function CookieSettingsButton({
  isLight,
  onClick,
  label = "Cookie settings",
}: {
  isLight: boolean
  onClick?: () => void
  label?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-sm text-left text-sm font-medium leading-5 underline decoration-1 [text-underline-position:from-font] transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#768692] ${
        isLight
          ? "text-[#0D1F2D]/55 hover:text-[#0D1F2D]"
          : "text-[#F6F7EB]/40 hover:text-[#F6F7EB]"
      }`}
    >
      <CookieGlyph size={16} strokeWidth={2} className="shrink-0" />
      {label}
    </button>
  )
}

export function FooterSection({
  surface = "dark",
}: {
  surface?: "dark" | "light"
}) {
  const isLight = surface === "light"
  const [showSupportModal, setShowSupportModal] = useState(false)

  const linkClass = `${styles.link} text-sm font-normal leading-5 transition-colors ${
    isLight
      ? "text-[#0D1F2D]/55 hover:text-[#0D1F2D]"
      : "text-[#F6F7EB]/40 hover:text-[#F6F7EB]"
  }`
  const headingClass = `text-sm font-semibold tracking-tight ${
    isLight ? "text-[#0D1F2D]" : "text-[#F6F7EB]"
  }`
  const iconStops = isLight ? ICON_STOPS_LIGHT : ICON_STOPS_DARK
  const borderClass = isLight ? "border-[#0D1F2D]/10" : "border-white/[0.07]"

  return (
    <>
      <footer
        className={`mx-auto w-full max-w-[1320px] border-t px-5 py-12 md:px-8 md:py-[70px] ${borderClass}`}
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-5">
          {/* Logo + description */}
          <div className="col-span-2 flex flex-col items-start gap-6">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={isLight ? "/logo-wide-light.svg" : "/logo-wide-dark.svg"}
              alt="ComplyRx"
              width={240}
              height={48}
              className="h-7 w-auto max-w-[min(240px,52vw)] md:h-8"
              draggable={false}
            />
            <div className="flex flex-col items-start gap-2">
              <p
                className={`text-left text-sm font-medium leading-[18px] ${isLight ? "text-[#5C7B7F]" : "text-[#F6F7EB]/50"}`}
              >
                Clinical trial intelligence for pharma and biotech teams.
              </p>
              <p
                className={`text-left text-xs leading-[16px] ${isLight ? "text-[#0D1F2D]/45" : "text-[#F6F7EB]/35"}`}
              >
                © 2026 Oliver Rymond, trading as ComplyRx. All rights reserved.
              </p>
            </div>
          </div>

          {/* Link columns */}
          {COLUMNS.map(({ title, Icon, links }) => (
            <nav key={title} className="flex flex-col items-start gap-4">
              <div className="flex flex-col items-start gap-2">
                <span aria-hidden>
                  <Icon size={24} {...iconStops} />
                </span>
                <h3 className={headingClass}>{title}</h3>
              </div>
              <ul className="flex flex-col items-start gap-2.5">
                {links.map((link) =>
                  link.cookieSettings ? (
                    <li key={link.name}>
                      <CookieSettingsButton
                        isLight={isLight}
                        onClick={openCookieSettings}
                        label={link.name}
                      />
                    </li>
                  ) : link.modal ? (
                    <li key={link.name}>
                      <button
                        type="button"
                        onClick={() => setShowSupportModal(true)}
                        className={`text-left ${linkClass}`}
                      >
                        {link.name}
                      </button>
                    </li>
                  ) : (
                    <li key={link.name}>
                      <a href={link.href} className={linkClass}>
                        {link.name}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </nav>
          ))}
        </div>
      </footer>

      {showSupportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative max-w-md w-full">
            {/* Neon glass card with primary color glow */}
            <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-card/90 backdrop-blur-md shadow-2xl">
              {/* Neon glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/10 via-transparent to-primary/5"></div>
              <div className="absolute inset-0 rounded-2xl shadow-[0_0_30px_rgba(92, 123, 127,0.3)]"></div>

              {/* Content */}
              <div className="relative p-8">
                {/* Close button */}
                <button
                  onClick={() => setShowSupportModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="h-5 w-5 text-foreground/70" />
                </button>

                {/* Header */}
                <h1 className="text-2xl font-semibold text-foreground mb-6 pr-8">Support</h1>

                {/* Content */}
                <div className="space-y-4">
                  <p className="text-foreground/80 text-sm leading-relaxed">
                    For any support queries, please contact us at:
                  </p>

                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <a
                      href="mailto:support@complyrx.io"
                      className="text-primary font-medium hover:text-primary/80 transition-colors"
                    >
                      support@complyrx.io
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
